import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  FiCheckCircle,
  FiEdit2,
  FiPlus,
  FiTrash2,
  FiTrendingUp,
  FiUsers,
  FiXCircle,
} from "react-icons/fi";
import chatSupportLogService from "../../api/services/chatSupportLogService";
import ConfirmWarningModal from "../../components/ConfirmWarningModal";
import { notifyApprovalsPendingChanged } from "../../utils/approvalsEvents";
import { notifyDailyTargetsChanged } from "../../utils/dailyTargetsEvents";
import {
  BannerSkeleton,
  FormSkeleton,
  MetricCardsSkeleton,
  PAGE_SIZE,
  PageAccessSkeleton,
  PerformanceCardsSkeleton,
  SubTabBtn,
  TablePagination,
  TableSkeleton,
  usePagedList,
} from "./chatSupportLogUi";

const THEME = { primary: "#FB565F" };
const inputCls =
  "border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-800 focus:ring-2 focus:ring-red-400 outline-none";

const ALLOWED = ["Admin", "Developer", "Chat Support", "CSR Team Lead"];
const RELATIONSHIP_STATUSES = [
  "Single",
  "In relationship",
  "Engaged",
  "Married",
  "Divorced",
  "Widowed",
  "Other",
];

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoIso(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function formatLogDate(value) {
  if (!value) return "—";
  if (typeof value === "string") return value.slice(0, 10);
  try {
    return new Date(value).toISOString().slice(0, 10);
  } catch {
    return String(value);
  }
}

function TabBtn({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 rounded-xl text-sm font-semibold ${
        active ? "text-white shadow" : "bg-gray-100 text-gray-600"
      }`}
      style={active ? { backgroundColor: THEME.primary } : {}}
    >
      {children}
    </button>
  );
}

function MetricProgress({ label, actual, target, percent, met }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-xs font-semibold text-gray-600">{label}</p>
        <span
          className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
            met ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
          }`}
        >
          {met ? "Target met" : "Below target"}
        </span>
      </div>
      <p className="text-lg font-bold text-gray-900">
        {actual}
        <span className="text-sm font-normal text-gray-400"> / {target}</span>
      </p>
      <div className="mt-2 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${percent}%`,
            backgroundColor: met ? "#10b981" : THEME.primary,
          }}
        />
      </div>
      <p className="text-[10px] text-gray-400 mt-1">{percent}% of daily target</p>
    </div>
  );
}

function ApprovalBadge({ status }) {
  const styles = {
    approved: "bg-emerald-100 text-emerald-800",
    rejected: "bg-rose-100 text-rose-800",
    pending: "bg-amber-100 text-amber-800",
  };
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${styles[status] || styles.pending}`}>
      {status || "pending"}
    </span>
  );
}

const emptyCustomerForm = () => ({
  customer_name: "",
  premium_duration: "",
  connected_with: "",
  relationship_status: "",
  customer_of: "",
  next_tab: "",
  notes: "",
});

export default function ChatSupportLogPage() {
  const userRole = localStorage.getItem("adminUserRole") || "Admin";
  const isAllowed = ALLOWED.includes(userRole);
  const isDeveloper = userRole === "Developer";
  const isAdmin = userRole === "Admin";
  const isSupervisor = isAdmin || isDeveloper;
  const isChatSupport = userRole === "Chat Support";
  const isTeamLead = userRole === "CSR Team Lead";

  const [access, setAccess] = useState(null);
  const [tab, setTab] = useState(
    isChatSupport ? "myLog" : isTeamLead || isSupervisor ? "team" : "performance"
  );
  const [selectedDate, setSelectedDate] = useState(todayIso());
  const [perfFrom, setPerfFrom] = useState(daysAgoIso(14));
  const [perfTo, setPerfTo] = useState(todayIso());
  const [historyFrom, setHistoryFrom] = useState(daysAgoIso(14));
  const [historyTo, setHistoryTo] = useState(todayIso());
  const [historyAgentId, setHistoryAgentId] = useState("");
  const [historyTab, setHistoryTab] = useState("targets");
  const [historyTargets, setHistoryTargets] = useState([]);
  const [historyReports, setHistoryReports] = useState([]);
  const [historyLogs, setHistoryLogs] = useState([]);
  const [accessLoading, setAccessLoading] = useState(true);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logs, setLogs] = useState([]);
  const [dailyReport, setDailyReport] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [targets, setTargets] = useState({
    chatMessages: 0,
    emailsAdded: 0,
    socialResponses: 0,
    randomRequests: 0,
  });
  const [targetsSet, setTargetsSet] = useState(false);
  const [teamReports, setTeamReports] = useState([]);
  const [pendingCustomerLogs, setPendingCustomerLogs] = useState([]);
  const [targetForm, setTargetForm] = useState({
    chatMessages: 1000,
    emailsAdded: 100,
    socialResponses: 100,
    randomRequests: 100,
  });
  const [applyTargetsToAll, setApplyTargetsToAll] = useState(false);
  const [includeSelfTarget, setIncludeSelfTarget] = useState(false);
  const [targetAgentId, setTargetAgentId] = useState(null);
  const [perfAgents, setPerfAgents] = useState([]);
  const [search, setSearch] = useState("");
  const [customerModal, setCustomerModal] = useState(null);
  const [reportForm, setReportForm] = useState({
    chat_messages_count: "",
    emails_added_count: "",
    social_responses_count: "",
    random_requests_approved: "",
    incomplete_target_notes: "",
  });
  const [confirmModal, setConfirmModal] = useState(null);
  const [approvalModal, setApprovalModal] = useState(null);

  const loadAccess = useCallback(async () => {
    setAccessLoading(true);
    try {
      const data = await chatSupportLogService.getAccess();
      setAccess(data.access);
      const t = data.access?.targets || targets;
      setTargets(t);
      setTargetsSet(Boolean(data.access?.targetsSet));
      setTargetForm({
        chatMessages: data.access?.targetsSet ? t.chatMessages : 1000,
        emailsAdded: data.access?.targetsSet ? t.emailsAdded : 100,
        socialResponses: data.access?.targetsSet ? t.socialResponses : 100,
        randomRequests: data.access?.targetsSet ? t.randomRequests : 100,
      });
      if (!selectedAgentId && data.access?.agents?.length) {
        const mine = data.access.agents.find((a) => a.agentId === data.access.adminId);
        setSelectedAgentId(mine?.agentId || data.access.agents[0]?.agentId || data.access.adminId);
      }
      return data.access;
    } finally {
      setAccessLoading(false);
    }
  }, [selectedAgentId]);

  const loadMyLog = useCallback(async () => {
    const agentId = isChatSupport ? access?.adminId : selectedAgentId;
    if (!agentId) return;
    setLoading(true);
    try {
      const data = await chatSupportLogService.getCustomerLogs({
        date: selectedDate,
        agent_id: agentId,
        search,
      });
      setLogs(data.logs || []);
      setDailyReport(data.dailyReport);
      setPerformance(data.performance);
      const t = data.targets || targets;
      setTargets(t);
      setTargetsSet(Boolean(data.targetsSet));
      const report = data.dailyReport;
      setReportForm({
        chat_messages_count: report?.chatMessagesCount ?? "",
        emails_added_count: report?.emailsAddedCount ?? "",
        social_responses_count: report?.socialResponsesCount ?? "",
        random_requests_approved: report?.randomRequestsApproved ?? "",
        incomplete_target_notes: report?.incompleteTargetNotes ?? "",
      });
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load log");
    } finally {
      setLoading(false);
    }
  }, [access?.adminId, isChatSupport, search, selectedAgentId, selectedDate]);

  const loadTeamReports = useCallback(async () => {
    setLoading(true);
    try {
      const data = await chatSupportLogService.getTeamReports({ date: selectedDate });
      setTeamReports(data.reports || []);
      setPendingCustomerLogs(data.pendingCustomerLogs || []);
      const agents = access?.agents || [];
      const firstAgentId = targetAgentId || agents[0]?.agentId || null;
      if (!targetAgentId && firstAgentId) setTargetAgentId(firstAgentId);
      const row = (data.reports || []).find((r) => r.agentId === (targetAgentId || firstAgentId));
      if (row?.targets) {
        setTargets(row.targets);
        setTargetsSet(Boolean(row.targetsSet));
        setTargetForm({
          chatMessages: row.targetsSet ? row.targets.chatMessages : 1000,
          emailsAdded: row.targetsSet ? row.targets.emailsAdded : 100,
          socialResponses: row.targetsSet ? row.targets.socialResponses : 100,
          randomRequests: row.targetsSet ? row.targets.randomRequests : 100,
        });
      } else {
        setTargetsSet(false);
        setTargetForm({
          chatMessages: 1000,
          emailsAdded: 100,
          socialResponses: 100,
          randomRequests: 100,
        });
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load team reports");
    } finally {
      setLoading(false);
    }
  }, [selectedDate, access?.agents, targetAgentId]);

  const loadPerformance = useCallback(async () => {
    setLoading(true);
    try {
      const data = await chatSupportLogService.getPerformance({ from: perfFrom, to: perfTo });
      setPerfAgents(data.agents || []);
      setTargets(data.targets || targets);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load performance");
    } finally {
      setLoading(false);
    }
  }, [perfFrom, perfTo]);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const params = { from: historyFrom, to: historyTo };
      if (historyAgentId) params.agent_id = historyAgentId;
      const data = await chatSupportLogService.getHistory(params);
      setHistoryTargets(data.targets || []);
      setHistoryReports(data.reports || []);
      setHistoryLogs(data.logs || []);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load history");
    } finally {
      setLoading(false);
    }
  }, [historyAgentId, historyFrom, historyTo]);

  useEffect(() => {
    if (!isAllowed) return;
    (async () => {
      try {
        await loadAccess();
      } catch (e) {
        toast.error(e?.response?.data?.message || "Access denied");
      }
    })();
  }, [isAllowed, loadAccess]);

  useEffect(() => {
    if (!isAllowed || !access) return;
    if (tab === "myLog") loadMyLog();
    else if (tab === "team") loadTeamReports();
    else if (tab === "history") loadHistory();
    else loadPerformance();
  }, [access, tab, loadMyLog, loadTeamReports, loadHistory, loadPerformance, isAllowed]);

  const isViewOnly = Boolean(access?.isViewOnly) || isDeveloper;
  const canViewHistory = Boolean(access?.canViewHistory) || isTeamLead || isSupervisor;
  const canEditLog = (Boolean(access?.canEditLogs) || (isChatSupport || isAdmin)) && !isViewOnly;
  const canSetTargets = (Boolean(access?.canSetTargets) || isTeamLead || isAdmin) && !isViewOnly;
  const canEditCounts = canSetTargets;
  const canApproveLogs = (Boolean(access?.canApproveLogs) || isTeamLead || isAdmin) && !isViewOnly;
  const canBrowseSupervisor = isTeamLead || isSupervisor;

  const logsPaging = usePagedList(
    logs,
    `logs-${selectedDate}-${selectedAgentId}-${search}-${tab}`,
    PAGE_SIZE
  );
  const pendingPaging = usePagedList(
    pendingCustomerLogs,
    `pending-${selectedDate}-${tab}`,
    PAGE_SIZE
  );
  const teamReportsPaging = usePagedList(
    teamReports,
    `team-${selectedDate}-${tab}`,
    PAGE_SIZE
  );
  const historyTargetsPaging = usePagedList(
    historyTargets,
    `ht-${historyFrom}-${historyTo}-${historyAgentId}-${historyTab}`,
    PAGE_SIZE
  );
  const historyReportsPaging = usePagedList(
    historyReports,
    `hr-${historyFrom}-${historyTo}-${historyAgentId}-${historyTab}`,
    PAGE_SIZE
  );
  const historyLogsPaging = usePagedList(
    historyLogs,
    `hl-${historyFrom}-${historyTo}-${historyAgentId}-${historyTab}`,
    PAGE_SIZE
  );
  const perfPaging = usePagedList(
    perfAgents,
    `perf-${perfFrom}-${perfTo}-${tab}`,
    PAGE_SIZE
  );

  const openAddCustomer = () => {
    setCustomerModal({ mode: "create", ...emptyCustomerForm() });
  };

  const openEditCustomer = (log) => {
    setCustomerModal({
      mode: "edit",
      id: log.id,
      customer_name: log.customerName || "",
      premium_duration: log.premiumDuration || "",
      connected_with: log.connectedWith || "",
      relationship_status: log.relationshipStatus || "",
      customer_of: log.customerOf || "",
      next_tab: log.nextTab || "",
      notes: log.notes || "",
    });
  };

  const saveCustomer = async () => {
    if (!customerModal?.customer_name?.trim()) {
      toast.error("Customer name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        log_date: selectedDate,
        customer_name: customerModal.customer_name,
        premium_duration: customerModal.premium_duration,
        connected_with: customerModal.connected_with,
        relationship_status: customerModal.relationship_status,
        customer_of: customerModal.customer_of,
        next_tab: customerModal.next_tab,
        notes: customerModal.notes,
        agent_id: isChatSupport ? access?.adminId : selectedAgentId,
      };
      if (customerModal.mode === "edit") {
        await chatSupportLogService.updateCustomerLog(customerModal.id, payload);
        toast.success("Customer log updated — sent for team lead approval");
      } else {
        await chatSupportLogService.createCustomerLog(payload);
        toast.success("Customer log added — pending team lead approval");
      }
      setCustomerModal(null);
      loadMyLog();
      notifyApprovalsPendingChanged({ delta: 1 });
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const requestDeleteCustomer = (log) => {
    setConfirmModal({
      title: `Remove "${log.customerName}"?`,
      message: "This customer log entry will be permanently deleted.",
      confirmLabel: "Delete",
      onConfirm: async () => {
        setSaving(true);
        try {
          await chatSupportLogService.deleteCustomerLog(log.id);
          toast.success("Deleted");
          setConfirmModal(null);
          loadMyLog();
        } catch (e) {
          toast.error(e?.response?.data?.message || "Failed to delete");
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const saveDailyReport = async () => {
    setSaving(true);
    try {
      const data = await chatSupportLogService.saveDailyReport({
        report_date: selectedDate,
        agent_id: selectedAgentId || access?.adminId,
        incomplete_target_notes: reportForm.incomplete_target_notes,
      });
      setDailyReport(data.report);
      setPerformance(data.performance);
      setReportForm((f) => ({
        ...f,
        chat_messages_count: data.report?.chatMessagesCount ?? f.chat_messages_count,
        emails_added_count: data.report?.emailsAddedCount ?? f.emails_added_count,
        social_responses_count: data.report?.socialResponsesCount ?? f.social_responses_count,
        random_requests_approved: data.report?.randomRequestsApproved ?? "",
        incomplete_target_notes: data.report?.incompleteTargetNotes ?? "",
      }));
      toast.success("Notes saved");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to save report");
    } finally {
      setSaving(false);
    }
  };

  const submitApproval = async () => {
    if (!approvalModal) return;
    setSaving(true);
    try {
      if (approvalModal.kind === "customerLog") {
        await chatSupportLogService.approveCustomerLog(approvalModal.log.id, {
          status: approvalModal.status,
          notes: approvalModal.notes,
        });
        toast.success(
          approvalModal.status === "approved" ? "Customer log approved" : "Customer log rejected"
        );
      } else if (approvalModal.report) {
        await chatSupportLogService.approveDailyReport(approvalModal.report.id, {
          status: approvalModal.status,
          notes: approvalModal.notes,
        });
        toast.success(approvalModal.status === "approved" ? "Report approved" : "Report rejected");
      }
      setApprovalModal(null);
      loadTeamReports();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to update approval");
    } finally {
      setSaving(false);
    }
  };

  const saveTargets = async () => {
    setSaving(true);
    try {
      const agentId = targetAgentId || selectedAgentId || access?.agents?.[0]?.agentId;
      const data = await chatSupportLogService.saveTargets({
        chatMessages: Number(targetForm.chatMessages) || 0,
        emailsAdded: Number(targetForm.emailsAdded) || 0,
        socialResponses: Number(targetForm.socialResponses) || 0,
        randomRequests: Number(targetForm.randomRequests) || 0,
        target_date: selectedDate,
        agent_id: applyTargetsToAll ? undefined : agentId,
        apply_to_all: applyTargetsToAll,
        include_self: applyTargetsToAll ? includeSelfTarget : undefined,
      });
      setTargets(data.targets);
      setTargetsSet(true);
      setTargetForm({
        chatMessages: data.targets.chatMessages,
        emailsAdded: data.targets.emailsAdded,
        socialResponses: data.targets.socialResponses,
        randomRequests: data.targets.randomRequests,
      });
      toast.success(
        applyTargetsToAll
          ? `Same targets applied to all team members for ${selectedDate}`
          : `Targets saved for selected user on ${selectedDate}`
      );
      notifyDailyTargetsChanged({ team: "chat_support", targetDate: selectedDate });
      if (tab === "team") loadTeamReports();
      if (tab === "myLog") loadMyLog();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to save targets");
    } finally {
      setSaving(false);
    }
  };

  const metricCards = useMemo(() => {
    if (!performance?.metrics) return [];
    return performance.metrics;
  }, [performance]);

  const tabs = useMemo(() => {
    const list = [];
    if (access?.tabs?.myLog || isChatSupport || isSupervisor || isTeamLead) {
      list.push({ key: "myLog", label: isTeamLead || isSupervisor ? "Agent Daily Log" : "My Daily Log" });
    }
    if (access?.tabs?.team || isTeamLead || isSupervisor) list.push({ key: "team", label: "Team Review" });
    if (access?.tabs?.history || canViewHistory) list.push({ key: "history", label: "History" });
    list.push({ key: "performance", label: "Performance" });
    return list;
  }, [access, canViewHistory, isChatSupport, isSupervisor, isTeamLead]);

  if (!isAllowed) {
    return <Navigate to="/dashboard" replace />;
  }

  if (accessLoading && !access) {
    return <PageAccessSkeleton />;
  }

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Chat Support Log</h1>
        <p className="text-sm text-gray-500 mt-1">
          Log customer interactions and track daily targets — agents, team leads, and admins can review performance.
          Chat / email / social counts come from the{" "}
          <a href="/chat-support-activity" className="text-red-600 font-medium underline-offset-2 hover:underline">
            Chat / Email / Social
          </a>{" "}
          sidebar page.
          {isViewOnly && (
            <span className="ml-1 text-violet-700 font-medium">View only — you can browse history but cannot edit.</span>
          )}
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        {tabs.map((t) => (
          <TabBtn key={t.key} active={tab === t.key} onClick={() => setTab(t.key)}>
            {t.label}
          </TabBtn>
        ))}
      </div>

      {tab === "myLog" && (
        <div className="space-y-5">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Date</label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className={inputCls}
              />
            </div>
            {!isChatSupport && access?.agents?.length > 0 && (
              <div className="min-w-[200px]">
                <label className="block text-xs font-semibold text-gray-500 mb-1">Agent</label>
                <select
                  value={selectedAgentId || ""}
                  onChange={(e) => setSelectedAgentId(Number(e.target.value))}
                  className={`${inputCls} w-full`}
                >
                  {access.agents.map((a) => (
                    <option key={a.agentId} value={a.agentId}>
                      {a.name || a.email}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-semibold text-gray-500 mb-1">Search customers</label>
              <input
                type="text"
                placeholder="Name, connected with, status…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`${inputCls} w-full`}
              />
            </div>
            {canEditLog && (
              <button
                type="button"
                onClick={openAddCustomer}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-semibold text-white"
                style={{ backgroundColor: THEME.primary }}
              >
                <FiPlus /> Add customer
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {loading && (canEditCounts || canBrowseSupervisor) ? (
              <div className="col-span-full">
                <MetricCardsSkeleton />
              </div>
            ) : (
              (canEditCounts || canBrowseSupervisor) &&
              metricCards.map((m) => <MetricProgress key={m.key} {...m} />)
            )}
          </div>

          {/* Members: read-only today's target for themselves */}
          {!canEditCounts && isChatSupport && (
            loading ? (
              <BannerSkeleton />
            ) : (
            <div
              className={`rounded-xl border px-4 py-3 text-sm ${
                targetsSet
                  ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                  : "border-amber-200 bg-amber-50 text-amber-900"
              }`}
            >
              {targetsSet ? (
                <>
                  <span className="font-semibold">Your target for {selectedDate} (set by team lead): </span>
                  {targets.chatMessages} chats · {targets.emailsAdded} emails · {targets.socialResponses} social ·{" "}
                  {targets.randomRequests} random requests
                </>
              ) : (
                <>Team lead has not set your daily target for {selectedDate} yet. You can still log customers.</>
              )}
            </div>
            )
          )}

          {/* Supervisors / developer: read-only target banner when not editing */}
          {!canEditCounts && canBrowseSupervisor && (
            loading ? (
              <BannerSkeleton />
            ) : (
            <div
              className={`rounded-xl border px-4 py-3 text-sm ${
                targetsSet
                  ? "border-slate-200 bg-slate-50 text-slate-800"
                  : "border-amber-200 bg-amber-50 text-amber-900"
              }`}
            >
              {targetsSet ? (
                <>
                  <span className="font-semibold">Target for {selectedDate}: </span>
                  {targets.chatMessages} chats · {targets.emailsAdded} emails · {targets.socialResponses} social ·{" "}
                  {targets.randomRequests} random requests
                  {isViewOnly && <span className="ml-2 text-xs text-violet-700">(view only)</span>}
                </>
              ) : (
                <>No target was set for this agent on {selectedDate}.</>
              )}
            </div>
            )
          )}

          <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-bold text-gray-900">Customer log</h2>
              <span className="text-xs text-gray-400">{logs.length} entries</span>
            </div>
            {loading ? (
              <TableSkeleton rows={8} cols={canEditLog ? 8 : 7} />
            ) : (
              <>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Name</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Premium</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Connected with</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Status</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Customer of</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Next tab</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">TL approval</th>
                      {canEditLog && (
                        <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500 w-24">Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {logsPaging.pageItems.map((log) => (
                      <tr key={log.id} className="border-t border-gray-100">
                        <td className="px-3 py-2 font-medium text-gray-900">{log.customerName}</td>
                        <td className="px-3 py-2 text-gray-600">{log.premiumDuration || "—"}</td>
                        <td className="px-3 py-2 text-gray-600">{log.connectedWith || "—"}</td>
                        <td className="px-3 py-2 text-gray-600">{log.relationshipStatus || "—"}</td>
                        <td className="px-3 py-2 text-gray-600">{log.customerOf || "—"}</td>
                        <td className="px-3 py-2 text-gray-600">{log.nextTab || "—"}</td>
                        <td className="px-3 py-2">
                          <ApprovalBadge status={log.tlApprovalStatus} />
                        </td>
                        {canEditLog && (
                          <td className="px-3 py-2 text-center">
                            <div className="flex justify-center gap-1">
                              <button
                                type="button"
                                onClick={() => openEditCustomer(log)}
                                className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50"
                                title="Edit"
                              >
                                <FiEdit2 size={14} />
                              </button>
                              <button
                                type="button"
                                onClick={() => requestDeleteCustomer(log)}
                                className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-50"
                                title="Delete"
                              >
                                <FiTrash2 size={14} />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                    {!logs.length && (
                      <tr>
                        <td colSpan={canEditLog ? 8 : 7} className="px-3 py-12 text-center text-gray-400">
                          No customer logs for this date. Click <strong>Add customer</strong> to start.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <TablePagination paging={logsPaging} label="customer logs" />
              </>
            )}
          </div>

          {canEditCounts && (
            loading ? (
              <FormSkeleton />
            ) : (
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-bold text-gray-900">Daily targets & counts</h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Chat / email / social / random totals come from the Chat / Email / Social sidebar page. Edit notes here.
                    {targetsSet
                      ? ` Current target for selected agent: ${targets.chatMessages} chats, ${targets.emailsAdded} emails, ${targets.socialResponses} social, ${targets.randomRequests} random.`
                      : ` No target set yet for this agent on ${selectedDate}.`}
                  </p>
                </div>
                {dailyReport?.tlApprovalStatus && (
                  <ApprovalBadge status={dailyReport.tlApprovalStatus} />
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Today chat with (messages)</label>
                  <input
                    type="number"
                    min={0}
                    value={reportForm.chat_messages_count}
                    readOnly
                    className={`${inputCls} w-full bg-gray-50 text-gray-600`}
                    placeholder={`Target: ${targets.chatMessages}`}
                    title="Synced from Chat / Email / Social activity records"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Emails added</label>
                  <input
                    type="number"
                    min={0}
                    value={reportForm.emails_added_count}
                    readOnly
                    className={`${inputCls} w-full bg-gray-50 text-gray-600`}
                    placeholder={`Target: ${targets.emailsAdded}`}
                    title="Synced from Chat / Email / Social activity records"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Social media responses</label>
                  <input
                    type="number"
                    min={0}
                    value={reportForm.social_responses_count}
                    readOnly
                    className={`${inputCls} w-full bg-gray-50 text-gray-600`}
                    placeholder={`Target: ${targets.socialResponses}`}
                    title="Synced from Chat / Email / Social activity records"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Random people requests</label>
                  <input
                    type="number"
                    min={0}
                    value={reportForm.random_requests_approved}
                    readOnly
                    className={`${inputCls} w-full bg-gray-50 text-gray-600`}
                    placeholder={`Target: ${targets.randomRequests}`}
                    title="Synced from Chat / Email / Social activity records"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-xs font-semibold text-gray-500 mb-1">
                  If target not complete (notes)
                </label>
                <textarea
                  value={reportForm.incomplete_target_notes}
                  onChange={(e) => setReportForm((f) => ({ ...f, incomplete_target_notes: e.target.value }))}
                  className={`${inputCls} w-full min-h-[72px]`}
                  placeholder="Explain any shortfall or blockers…"
                />
              </div>
              <button
                type="button"
                onClick={saveDailyReport}
                disabled={saving}
                className="px-5 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                style={{ backgroundColor: THEME.primary }}
              >
                {saving ? "Saving…" : "Save notes"}
              </button>
            </div>
            )
          )}
        </div>
      )}

      {tab === "team" && (
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Review date</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className={inputCls}
            />
          </div>

          {canSetTargets && (
            loading ? (
              <FormSkeleton />
            ) : (
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="mb-4">
                <h2 className="font-bold text-gray-900">Set daily targets for {selectedDate}</h2>
                <p className="text-xs text-gray-500 mt-0.5">
              Set different targets per Chat Support user, or apply the same targets to all Chat Support agents
              (CSR agents are not included — use CSR Outreach → Daily Targets).
                </p>
              </div>
              <div className="flex flex-wrap gap-3 items-end mb-4">
                <div className="min-w-[220px]">
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Agent</label>
                  <select
                    value={targetAgentId || ""}
                    disabled={applyTargetsToAll}
                    onChange={(e) => {
                      const id = Number(e.target.value);
                      setTargetAgentId(id);
                      const row = teamReports.find((r) => r.agentId === id);
                      if (row?.targets) {
                        setTargets(row.targets);
                        setTargetsSet(Boolean(row.targetsSet));
                        setTargetForm({
                          chatMessages: row.targetsSet ? row.targets.chatMessages : 1000,
                          emailsAdded: row.targetsSet ? row.targets.emailsAdded : 100,
                          socialResponses: row.targetsSet ? row.targets.socialResponses : 100,
                          randomRequests: row.targetsSet ? row.targets.randomRequests : 100,
                        });
                      }
                    }}
                    className={`${inputCls} w-full`}
                  >
                    {(access?.agents || []).map((a) => (
                      <option key={a.agentId} value={a.agentId}>
                        {a.isTeamLeadSelf ? `${a.name || a.email} (You)` : a.name || a.email}
                      </option>
                    ))}
                  </select>
                </div>
                <label className="inline-flex items-center gap-2 text-sm text-gray-700 pb-2">
                  <input
                    type="checkbox"
                    checked={applyTargetsToAll}
                    onChange={(e) => setApplyTargetsToAll(e.target.checked)}
                  />
                  Apply same targets to all team members
                </label>
                {applyTargetsToAll ? (
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700 pb-2">
                    <input
                      type="checkbox"
                      checked={includeSelfTarget}
                      onChange={(e) => setIncludeSelfTarget(e.target.checked)}
                    />
                    Include my own target
                  </label>
                ) : null}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Chat messages</label>
                  <input
                    type="number"
                    min={0}
                    value={targetForm.chatMessages}
                    onChange={(e) => setTargetForm((f) => ({ ...f, chatMessages: e.target.value }))}
                    className={`${inputCls} w-full`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Emails added</label>
                  <input
                    type="number"
                    min={0}
                    value={targetForm.emailsAdded}
                    onChange={(e) => setTargetForm((f) => ({ ...f, emailsAdded: e.target.value }))}
                    className={`${inputCls} w-full`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Social responses</label>
                  <input
                    type="number"
                    min={0}
                    value={targetForm.socialResponses}
                    onChange={(e) => setTargetForm((f) => ({ ...f, socialResponses: e.target.value }))}
                    className={`${inputCls} w-full`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Random requests</label>
                  <input
                    type="number"
                    min={0}
                    value={targetForm.randomRequests}
                    onChange={(e) => setTargetForm((f) => ({ ...f, randomRequests: e.target.value }))}
                    className={`${inputCls} w-full`}
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={saveTargets}
                disabled={saving || (!applyTargetsToAll && !targetAgentId && !access?.agents?.length)}
                className="px-5 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                style={{ backgroundColor: THEME.primary }}
              >
                {saving
                  ? "Saving…"
                  : applyTargetsToAll
                    ? "Save same targets for all"
                    : "Save targets for selected user"}
              </button>
            </div>
            )
          )}

          <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-bold text-gray-900">Pending customer logs</h2>
              <span className="text-xs text-gray-400">{pendingCustomerLogs.length} awaiting approval</span>
            </div>
            {loading ? (
              <TableSkeleton rows={5} cols={6} />
            ) : (
              <>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Agent</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Customer</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Connected with</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Status</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Notes</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingPaging.pageItems.map((log) => (
                      <tr key={log.id} className="border-t border-gray-100">
                        <td className="px-3 py-2">
                          <p className="font-semibold text-gray-900">{log.agentName || "—"}</p>
                          <p className="text-xs text-gray-400">{log.agentEmail}</p>
                        </td>
                        <td className="px-3 py-2 font-medium text-gray-900">{log.customerName}</td>
                        <td className="px-3 py-2 text-gray-600">{log.connectedWith || "—"}</td>
                        <td className="px-3 py-2 text-gray-600">{log.relationshipStatus || "—"}</td>
                        <td className="px-3 py-2 text-gray-600 max-w-[200px] truncate">{log.notes || "—"}</td>
                        <td className="px-3 py-2 text-center">
                          {canApproveLogs ? (
                            <div className="flex justify-center gap-1">
                              <button
                                type="button"
                                onClick={() =>
                                  setApprovalModal({
                                    kind: "customerLog",
                                    log,
                                    status: "approved",
                                    notes: "",
                                  })
                                }
                                className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50"
                                title="Approve"
                              >
                                <FiCheckCircle size={16} />
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  setApprovalModal({
                                    kind: "customerLog",
                                    log,
                                    status: "rejected",
                                    notes: "",
                                  })
                                }
                                className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-50"
                                title="Reject"
                              >
                                <FiXCircle size={16} />
                              </button>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-300">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {!pendingCustomerLogs.length && (
                      <tr>
                        <td colSpan={6} className="px-3 py-8 text-center text-gray-400">
                          No pending customer logs for this date.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <TablePagination paging={pendingPaging} label="pending logs" />
              </>
            )}
          </div>

          <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100">
              <h2 className="font-bold text-gray-900">Daily reports</h2>
            </div>
            {loading ? (
              <TableSkeleton rows={6} cols={8} />
            ) : (
              <>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Agent</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500">Chats</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500">Emails</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500">Social</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500">Random req.</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500">Overall</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500">TL approval</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamReportsPaging.pageItems.map((r) => (
                      <tr key={r.id || `agent-${r.agentId}`} className="border-t border-gray-100">
                        <td className="px-3 py-2">
                          <p className="font-semibold text-gray-900">{r.agentName}</p>
                          <p className="text-xs text-gray-400">{r.agentEmail}</p>
                        </td>
                        <td className="px-3 py-2 text-center">
                          {r.chatMessagesCount}/{r.targets?.chatMessages ?? 0}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {r.emailsAddedCount}/{r.targets?.emailsAdded ?? 0}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {r.socialResponsesCount}/{r.targets?.socialResponses ?? 0}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {r.randomRequestsApproved}/{r.targets?.randomRequests ?? 0}
                        </td>
                        <td className="px-3 py-2 text-center font-bold">
                          {r.performance?.overallPercent ?? 0}%
                        </td>
                        <td className="px-3 py-2 text-center">
                          <ApprovalBadge status={r.tlApprovalStatus} />
                        </td>
                        <td className="px-3 py-2 text-center">
                          {canApproveLogs && r.id ? (
                            <div className="flex justify-center gap-1">
                              <button
                                type="button"
                                onClick={() =>
                                  setApprovalModal({ report: r, status: "approved", notes: "" })
                                }
                                className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50"
                                title="Approve"
                              >
                                <FiCheckCircle size={16} />
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  setApprovalModal({ report: r, status: "rejected", notes: "" })
                                }
                                className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-50"
                                title="Reject"
                              >
                                <FiXCircle size={16} />
                              </button>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-300">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {!teamReports.length && (
                      <tr>
                        <td colSpan={8} className="px-3 py-12 text-center text-gray-400">
                          No daily reports submitted for this date.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <TablePagination paging={teamReportsPaging} label="daily reports" />
              </>
            )}
          </div>
        </div>
      )}

      {tab === "history" && (
        <div className="space-y-4">
          <div className="rounded-xl border border-violet-100 bg-violet-50 px-4 py-3 text-sm text-violet-900">
            View-only history of past targets, daily performance, and customer logs for team lead / admin / developer.
          </div>
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">From</label>
              <input
                type="date"
                value={historyFrom}
                onChange={(e) => setHistoryFrom(e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">To</label>
              <input
                type="date"
                value={historyTo}
                onChange={(e) => setHistoryTo(e.target.value)}
                className={inputCls}
              />
            </div>
            {access?.agents?.length > 0 && (
              <div className="min-w-[200px]">
                <label className="block text-xs font-semibold text-gray-500 mb-1">Agent</label>
                <select
                  value={historyAgentId}
                  onChange={(e) => setHistoryAgentId(e.target.value)}
                  className={`${inputCls} w-full`}
                >
                  <option value="">All agents</option>
                  {access.agents.map((a) => (
                    <option key={a.agentId} value={a.agentId}>
                      {a.name || a.email}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <SubTabBtn
              active={historyTab === "targets"}
              onClick={() => setHistoryTab("targets")}
              count={historyTargets.length}
            >
              Targets
            </SubTabBtn>
            <SubTabBtn
              active={historyTab === "performance"}
              onClick={() => setHistoryTab("performance")}
              count={historyReports.length}
            >
              Performance
            </SubTabBtn>
            <SubTabBtn
              active={historyTab === "logs"}
              onClick={() => setHistoryTab("logs")}
              count={historyLogs.length}
            >
              Customer logs
            </SubTabBtn>
          </div>

          {historyTab === "targets" && (
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <h2 className="font-bold text-gray-900">Target history</h2>
                <span className="text-xs text-gray-400">{historyTargets.length} records</span>
              </div>
              {loading ? (
                <TableSkeleton rows={8} cols={6} />
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Date</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Agent</th>
                          <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500">Chats</th>
                          <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500">Emails</th>
                          <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500">Social</th>
                          <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500">Random</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historyTargetsPaging.pageItems.map((t) => (
                          <tr key={t.id || `${t.agentId}-${t.targetDate}`} className="border-t border-gray-100">
                            <td className="px-3 py-2 text-gray-700">{formatLogDate(t.targetDate)}</td>
                            <td className="px-3 py-2">
                              <p className="font-medium text-gray-900">{t.agentName || "—"}</p>
                              <p className="text-xs text-gray-400">{t.agentEmail}</p>
                            </td>
                            <td className="px-3 py-2 text-center">{t.chatMessages}</td>
                            <td className="px-3 py-2 text-center">{t.emailsAdded}</td>
                            <td className="px-3 py-2 text-center">{t.socialResponses}</td>
                            <td className="px-3 py-2 text-center">{t.randomRequests}</td>
                          </tr>
                        ))}
                        {!historyTargets.length && (
                          <tr>
                            <td colSpan={6} className="px-3 py-8 text-center text-gray-400">
                              No targets set in this date range.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <TablePagination paging={historyTargetsPaging} label="targets" />
                </>
              )}
            </div>
          )}

          {historyTab === "performance" && (
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <h2 className="font-bold text-gray-900">Performance history (daily reports)</h2>
                <span className="text-xs text-gray-400">{historyReports.length} reports</span>
              </div>
              {loading ? (
                <TableSkeleton rows={8} cols={8} />
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Date</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Agent</th>
                          <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500">Chats</th>
                          <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500">Emails</th>
                          <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500">Social</th>
                          <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500">Random</th>
                          <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500">Score</th>
                          <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500">TL</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historyReportsPaging.pageItems.map((r) => (
                          <tr key={r.id || `${r.agentId}-${r.reportDate}`} className="border-t border-gray-100">
                            <td className="px-3 py-2 text-gray-700">{formatLogDate(r.reportDate)}</td>
                            <td className="px-3 py-2">
                              <p className="font-medium text-gray-900">{r.agentName || "—"}</p>
                              <p className="text-xs text-gray-400">{r.agentEmail}</p>
                            </td>
                            <td className="px-3 py-2 text-center">
                              {r.chatMessagesCount}/{r.targets?.chatMessages ?? 0}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {r.emailsAddedCount}/{r.targets?.emailsAdded ?? 0}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {r.socialResponsesCount}/{r.targets?.socialResponses ?? 0}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {r.randomRequestsApproved}/{r.targets?.randomRequests ?? 0}
                            </td>
                            <td className="px-3 py-2 text-center font-bold">
                              {r.performance?.overallPercent ?? 0}%
                            </td>
                            <td className="px-3 py-2 text-center">
                              <ApprovalBadge status={r.tlApprovalStatus} />
                            </td>
                          </tr>
                        ))}
                        {!historyReports.length && (
                          <tr>
                            <td colSpan={8} className="px-3 py-8 text-center text-gray-400">
                              No daily reports in this date range.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <TablePagination paging={historyReportsPaging} label="reports" />
                </>
              )}
            </div>
          )}

          {historyTab === "logs" && (
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <h2 className="font-bold text-gray-900">Customer log history</h2>
                <span className="text-xs text-gray-400">{historyLogs.length} entries</span>
              </div>
              {loading ? (
                <TableSkeleton rows={8} cols={6} />
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Date</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Agent</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Customer</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Connected with</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Status</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">TL</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historyLogsPaging.pageItems.map((log) => (
                          <tr key={log.id} className="border-t border-gray-100">
                            <td className="px-3 py-2 text-gray-700">{formatLogDate(log.logDate)}</td>
                            <td className="px-3 py-2">
                              <p className="font-medium text-gray-900">{log.agentName || "—"}</p>
                              <p className="text-xs text-gray-400">{log.agentEmail}</p>
                            </td>
                            <td className="px-3 py-2 font-medium text-gray-900">{log.customerName}</td>
                            <td className="px-3 py-2 text-gray-600">{log.connectedWith || "—"}</td>
                            <td className="px-3 py-2 text-gray-600">{log.relationshipStatus || "—"}</td>
                            <td className="px-3 py-2">
                              <ApprovalBadge status={log.tlApprovalStatus} />
                            </td>
                          </tr>
                        ))}
                        {!historyLogs.length && (
                          <tr>
                            <td colSpan={6} className="px-3 py-8 text-center text-gray-400">
                              No customer logs in this date range.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <TablePagination paging={historyLogsPaging} label="customer logs" />
                </>
              )}
            </div>
          )}
        </div>
      )}

      {tab === "performance" && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">From</label>
              <input type="date" value={perfFrom} onChange={(e) => setPerfFrom(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">To</label>
              <input type="date" value={perfTo} onChange={(e) => setPerfTo(e.target.value)} className={inputCls} />
            </div>
          </div>
          {loading ? (
            <PerformanceCardsSkeleton />
          ) : (
            <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {perfPaging.pageItems.map((agent) => (
                <div key={agent.agentId} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                      <p className="font-bold text-gray-900">{agent.agentName || "Agent"}</p>
                      <p className="text-xs text-gray-400">{agent.agentEmail}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold" style={{ color: THEME.primary }}>
                        {agent.performance?.overallPercent ?? 0}%
                      </p>
                      <p className="text-[10px] text-gray-400">avg daily score</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {agent.performance?.metrics?.map((m) => (
                      <div key={m.key} className="rounded-lg bg-gray-50 px-3 py-2">
                        <p className="text-[10px] text-gray-500">{m.label}</p>
                        <p className="text-sm font-bold text-gray-800">
                          {m.actual} <span className="font-normal text-gray-400">/ {m.target}</span>
                        </p>
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                    <span className="inline-flex items-center gap-1">
                      <FiUsers /> {agent.customerLogCount} customer logs
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <FiTrendingUp /> {agent.daysReported} days reported
                    </span>
                    {agent.latestReport?.tlApprovalStatus && (
                      <ApprovalBadge status={agent.latestReport.tlApprovalStatus} />
                    )}
                  </div>
                  {agent.latestReport?.incompleteTargetNotes && (
                    <p className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg p-2">
                      {agent.latestReport.incompleteTargetNotes}
                    </p>
                  )}
                </div>
              ))}
              {!perfAgents.length && (
                <p className="col-span-full text-center text-gray-400 py-12">No performance data for this period.</p>
              )}
            </div>
            <TablePagination paging={perfPaging} label="agents" />
            </>
          )}
        </div>
      )}

      {customerModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-5 max-h-[90vh] overflow-y-auto">
            <h3 className="font-bold text-gray-900 mb-4">
              {customerModal.mode === "edit" ? "Edit customer log" : "Add customer log"}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Name *</label>
                <input
                  value={customerModal.customer_name}
                  onChange={(e) => setCustomerModal((m) => ({ ...m, customer_name: e.target.value }))}
                  className={`${inputCls} w-full`}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Premium duration</label>
                <input
                  value={customerModal.premium_duration}
                  onChange={(e) => setCustomerModal((m) => ({ ...m, premium_duration: e.target.value }))}
                  className={`${inputCls} w-full`}
                  placeholder="e.g. 3 months"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Connected with</label>
                <input
                  value={customerModal.connected_with}
                  onChange={(e) => setCustomerModal((m) => ({ ...m, connected_with: e.target.value }))}
                  className={`${inputCls} w-full`}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Status</label>
                <select
                  value={customerModal.relationship_status}
                  onChange={(e) => setCustomerModal((m) => ({ ...m, relationship_status: e.target.value }))}
                  className={`${inputCls} w-full`}
                >
                  <option value="">Select…</option>
                  {RELATIONSHIP_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Customer of</label>
                <input
                  value={customerModal.customer_of}
                  onChange={(e) => setCustomerModal((m) => ({ ...m, customer_of: e.target.value }))}
                  className={`${inputCls} w-full`}
                  placeholder="TL / agent name"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Next tab</label>
                <input
                  value={customerModal.next_tab}
                  onChange={(e) => setCustomerModal((m) => ({ ...m, next_tab: e.target.value }))}
                  className={`${inputCls} w-full`}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Notes</label>
                <textarea
                  value={customerModal.notes}
                  onChange={(e) => setCustomerModal((m) => ({ ...m, notes: e.target.value }))}
                  className={`${inputCls} w-full min-h-[60px]`}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => setCustomerModal(null)}
                className="px-4 py-2 text-sm rounded-lg border"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveCustomer}
                disabled={saving}
                className="px-4 py-2 text-sm rounded-lg text-white disabled:opacity-50"
                style={{ backgroundColor: THEME.primary }}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {approvalModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5">
            <h3 className="font-bold text-gray-900 mb-2">
              {approvalModal.kind === "customerLog"
                ? approvalModal.status === "approved"
                  ? "Approve customer log"
                  : "Reject customer log"
                : approvalModal.status === "approved"
                  ? "Approve daily report"
                  : "Reject daily report"}
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              {approvalModal.kind === "customerLog"
                ? `${approvalModal.log?.agentName || "Agent"} — ${approvalModal.log?.customerName || "Customer"}`
                : `${approvalModal.report?.agentName} — ${selectedDate}`}
            </p>
            <textarea
              value={approvalModal.notes}
              onChange={(e) => setApprovalModal((m) => ({ ...m, notes: e.target.value }))}
              className={`${inputCls} w-full min-h-[80px] mb-4`}
              placeholder="TL notes (optional)"
            />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setApprovalModal(null)} className="px-4 py-2 text-sm rounded-lg border">
                Cancel
              </button>
              <button
                type="button"
                onClick={submitApproval}
                disabled={saving}
                className="px-4 py-2 text-sm rounded-lg text-white disabled:opacity-50"
                style={{
                  backgroundColor: approvalModal.status === "approved" ? "#10b981" : THEME.primary,
                }}
              >
                {saving ? "Saving…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmWarningModal
        open={Boolean(confirmModal)}
        title={confirmModal?.title}
        message={confirmModal?.message}
        confirmLabel={confirmModal?.confirmLabel || "Delete"}
        variant="danger"
        confirming={saving}
        onClose={() => {
          if (!saving) setConfirmModal(null);
        }}
        onConfirm={() => confirmModal?.onConfirm?.()}
      />
    </div>
  );
}
